import React from 'react';
import cn from 'class-names';

import { ProgressBarComponent } from '@uikit/old/ProgressBar/ProgressBar';

import { PublisherWebsiteImportType } from '@ts/publishers.types';
import { isImportInProgress } from '@hooks/usePublisherWebsiteImport';

import { getImportStatusLabel, getProcessedRows, getProgressPercentage } from '../../utils';

import './ImportStatusDetails.scss';

function ImportStatusDetails({ item }: { item: PublisherWebsiteImportType }): JSX.Element {
  const inProgress = isImportInProgress(item);
  const hasProblems = item.skippedRows > 0 || item.failedRows > 0;

  return (
    <div className="import-status-details" data-testid="publisher-import-report">
      <div className="import-status-details__file">
        <span className="import-status-details__file-name" title={item.fileName}>
          {item.fileName || 'Websites import'}
        </span>
        <span
          className={cn('import-status-details__status', {
            'import-status-details__status--in-progress': inProgress,
          })}
        >
          {getImportStatusLabel(item)}
        </span>
      </div>

      <div className="import-status-details__progress-title">
        <span>
          {getProcessedRows(item)} of {item.totalRows} websites processed
        </span>
        <span>{getProgressPercentage(item)}%</span>
      </div>
      <ProgressBarComponent
        containerClassName="import-status-details__progress-bar"
        percentage={getProgressPercentage(item)}
      />

      <div className="import-status-details__counters">
        <div className="import-status-details__counter">
          <span className="import-status-details__counter-value import-status-details__counter-value--created">
            {item.createdRows}
          </span>
          <span className="import-status-details__counter-label">Created</span>
        </div>
        <div className="import-status-details__counter">
          <span className="import-status-details__counter-value">{item.skippedRows}</span>
          <span className="import-status-details__counter-label">Skipped</span>
        </div>
        <div className="import-status-details__counter">
          <span className="import-status-details__counter-value import-status-details__counter-value--failed">
            {item.failedRows}
          </span>
          <span className="import-status-details__counter-label">Failed</span>
        </div>
      </div>

      <p className="import-status-details__hint">
        {inProgress
          ? 'Websites are created one by one in the background. You can close this window; the import keeps running.'
          : null}
        {!inProgress && hasProblems
          ? 'The report lists the reason for every skipped and failed row. Fix those rows in the report and upload it again: the Status and Error Reason columns are ignored.'
          : null}
        {!inProgress && item.failedRows > 0 ? (
          <>
            {' '}
            Rows that did not fit into the website limit of your account are marked as failed.
            Contact support to add more websites.
          </>
        ) : null}
        {!inProgress && !hasProblems
          ? 'All websites were created as drafts. Verify each website to make it available for orders.'
          : null}
      </p>
    </div>
  );
}

export default ImportStatusDetails;
